import { Component, Input, Output, EventEmitter, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissionService, NavItem } from '../../services/permission.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss']
})
export class Sidebar implements OnInit {
  @Input() isOpen: boolean = true;
  @Input() isCollapsed: boolean = false;
  @Output() linkClicked = new EventEmitter<void>();

  private permissionService = inject(PermissionService);
  private authService = inject(AuthService);
  private router = inject(Router);

  shouldCheckPermissions = false; // Flag to check if we need to apply ACL
  navItems: NavItem[] = []; // Kept for backwards compatibility if needed internally
  entityItems: NavItem[] = [];
  masterItems: NavItem[] = [];

  ngOnInit(): void {
    // Check if current user has roleId 1, 2, or 3
    const user = this.authService.getCurrentUser();
    const roleId = user?.roleId;
    this.shouldCheckPermissions = (roleId == 1 || roleId == 2 || roleId == 3);

    // Load navigation items from permissions
    this.loadNavItems();

    // Subscribe to permission changes to update nav items
    this.permissionService.permissions$.subscribe(() => {
      this.loadNavItems();
      this.checkActiveRoute();
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkActiveRoute();
    });

    this.checkActiveRoute();
  }

  private checkActiveRoute(): void {
    const currentUrl = this.router.url;

    // Check entity items
    this.entityItems.forEach(item => {
      if (item.children) {
        if (item.children.some(child => currentUrl.includes(child.route))) {
          this.expandedItems.add(item.label);
        }
      }
    });

    // Check master items
    this.masterItems.forEach(item => {
      if (item.children) {
        if (item.children.some(child => currentUrl.includes(child.route))) {
          this.expandedItems.add(item.label);
        }
      }
    });
  }


  /**
   * Load navigation items based on user permissions
   */
  private loadNavItems(): void {
    let allItems: NavItem[] = [];
    if (this.shouldCheckPermissions) {
      // For roles 1, 2, 3: Generate nav from API permissions
      allItems = this.permissionService.generateNavItems();
    } else {
      // For other roles: Show all items (fallback to default nav)
      allItems = this.getDefaultNavItems();
    }

    this.navItems = allItems;

    const entityRoutes = [
      '/patients',
      '/claim-form-rules',
      '/ocr-settings',
      '/claims',
      '/documents',
      '/reports'
    ];

    this.entityItems = allItems.filter(item => entityRoutes.includes(item.route));
    this.masterItems = allItems.filter(item => !entityRoutes.includes(item.route));
  }

  /**
   * Get default navigation items (for roles without ACL)
   */
  private getDefaultNavItems(): NavItem[] {
    return [
      {
        label: 'Manage Clinics',
        route: '/clinics',
        icon: 'local_hospital',
        moduleName: 'Manage Clinics'
      },
      {
        label: 'Manage Codes',
        route: '/codes',
        icon: 'list_alt',
        moduleName: 'Manage Codes'
      },
      {
        label: 'Global Claim Form Rules',
        route: '/claim-form-rules',
        icon: 'verified_user',
        moduleName: 'Global Claim Form Rules'
      },
      {
        label: 'Payer & Claim Type Rules',
        route: '/payer-rules',
        icon: 'policy',
        moduleName: 'Payer & Claim Type Rules'
      },
      {
        label: 'Manage Staff Users',
        route: '/staff-users',
        icon: 'manage_accounts',
        moduleName: 'Manage Staff Users'
      },
      {
        label: 'Manage Providers',
        route: '/providers',
        icon: 'medical_services',
        moduleName: 'Manage Providers'
      },
      {
        label: 'Manage Patients',
        route: '/patients',
        icon: 'personal_injury',
        moduleName: 'Manage Patients'
      },
      {
        label: 'Documents Configuration',
        route: '/documents',
        icon: 'folder_special',
        moduleName: 'Documents Configuration'
      },
      {
        label: 'OCR & AI Settings',
        route: '/ocr-settings',
        icon: 'language',
        moduleName: 'OCR & AI Settings'
      },
      {
        label: 'Manage Claims',
        route: '/claims',
        icon: 'verified_user',
        moduleName: 'Manage Claims'
      },
      {
        label: 'Claim Form Templates',
        route: '/claim-form-templates',
        icon: 'text_snippet',
        moduleName: 'Claim Form Templates'
      },
      {
        label: 'Manage Email Templates',
        route: '/email-templates',
        icon: 'mail',
        moduleName: 'Manage Email Templates'
      },
      {
        label: 'Audit Logs',
        route: '/audit-logs',
        icon: 'article',
        moduleName: 'Audit Logs'
      },
      {
        label: 'Reports',
        route: '/reports',
        icon: 'assessment',
        moduleName: 'Reports',
        children: [
          // { label: 'Documents Reports', route: '/reports/documents', icon: 'description', moduleName: 'Reports' },
          { label: 'Claim Reports', route: '/reports/claims', icon: 'assessment', moduleName: 'Reports' },
          { label: 'Split Claim Reports', route: '/reports/split-claims', icon: 'assessment', moduleName: 'Reports' },
          // { label: 'Payer Reports', route: '/reports/payers', icon: 'business', moduleName: 'Reports' },
          { label: 'Financial Reports', route: '/reports/financial', icon: 'trending_up', moduleName: 'Reports' },
          // { label: 'AI Performance Reports', route: '/reports/ai-performance', icon: 'analytics', moduleName: 'Reports' },
          { label: 'Token Reports', route: '/reports/token-reports', icon: 'assessment', moduleName: 'Reports' }
        ]
      }
    ];
  }

  onLinkClick(): void {
    if (this.isCollapsed) {
      this.linkClicked.emit();
    }
  }

  expandedItems: Set<string> = new Set();

  toggleExpand(label: string): void {
    if (this.expandedItems.has(label)) {
      this.expandedItems.delete(label);
    } else {
      this.expandedItems.add(label);
    }
  }

  isExpanded(label: string): boolean {
    return this.expandedItems.has(label);
  }
}
